/**
 * Shared building blocks for the alternate landing-page templates
 * (LandingPageSkLodge, LandingPageAsamika). Copied out of LandingPage.jsx
 * (the original "classic" template) rather than moved — classic keeps its
 * own internal copies untouched, so nothing about the existing, already-
 * verified design can regress from this file existing.
 *
 * Everything visual here takes style props with classic-flavoured defaults,
 * so each template can recolour these to its own palette without forking
 * the logic.
 */
import React, { useState, useEffect, useRef } from "react";
import { Star, X, ExternalLink } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE = process.env.REACT_APP_BACKEND_URL || "";

export function imgSrc(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BE}${url}`;
}

/**
 * The two public fetches every landing template needs, in one place:
 * rooms (404 => this tenant has the direct-booking feature switched off)
 * and live reviews. Same calls and same 404 handling LandingPage.jsx does
 * inline — extracted so the alternate templates don't each reimplement it.
 */
export function useLandingData() {
  const [rooms, setRooms] = useState([]);
  const [bookingDisabled, setBookingDisabled] = useState(false);
  const [liveReviews, setLiveReviews] = useState([]);

  useEffect(() => {
    fetch(`${API}/rooms/public`)
      .then((r) => {
        if (r.status === 404) { setBookingDisabled(true); return { data: [] }; }
        return r.json();
      })
      .then((j) => setRooms(j.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/reviews/public`)
      .then((r) => r.json())
      .then((j) => {
        setLiveReviews((j.data || []).map((r) => ({
          name: r.guestName,
          rating: r.rating,
          date: new Date(r.createdAt).toLocaleDateString("en-IN", { month: "long", year: "numeric" }),
          avatar: r.guestName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(),
          text: r.comment || "Great stay!",
          via: "Verified Guest",
        })));
      })
      .catch(() => {});
  }, []);

  return { rooms, bookingDisabled, liveReviews };
}

const SLIDESHOW_INTERVAL_MS = 3500;

// Auto-advancing crossfade slideshow for a room card's photos — stacked in
// place so there's no layout shift, photo 0 (the admin-chosen thumbnail)
// first, and prefers-reduced-motion stops the auto-advance entirely.
export function RoomPhotoSlideshow({ photos, alt, dotColor = "#D9A441" }) {
  const [index, setIndex] = useState(0);
  const reduceMotion = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (photos.length < 2 || reduceMotion.current) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % photos.length), SLIDESHOW_INTERVAL_MS);
    return () => clearInterval(id);
  }, [photos.length]);

  if (photos.length <= 1) {
    return (
      <img src={photos[0]} alt={alt} loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
    );
  }

  return (
    <>
      {photos.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={i === 0 ? alt : ""}
          loading={i === 0 ? "lazy" : "eager"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 absolute inset-0"
          style={{ opacity: i === index ? 1 : 0, transition: "opacity 1s ease" }}
        />
      ))}
      <div className="absolute bottom-3 right-3 flex gap-1.5 z-10">
        {photos.map((_, i) => (
          <span key={i} className="rounded-full transition-all"
            style={{ width: i === index ? 14 : 6, height: 6, background: i === index ? dotColor : "rgba(255,255,255,.6)" }} />
        ))}
      </div>
    </>
  );
}

export function StarRow({ n, size = 14, filledClass = "text-amber-500 fill-amber-500", emptyClass = "text-stone-200 fill-stone-200" }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} className={i < n ? filledClass : emptyClass} />
      ))}
    </div>
  );
}

/**
 * Booking-reference + WhatsApp verification before letting a past guest
 * write a review — same real `/reviews/lookup` flow the classic template
 * uses; only the accent classes are parameterised.
 */
export function ReviewLookupModal({ onClose, accent = {} }) {
  const { googleReviewUrl } = useSettings();
  const [bookingRef, setBookingRef] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    panel = "bg-white border-stone-100",
    heading = "text-stone-900",
    focusRing = "focus:ring-amber-400/60 focus:border-amber-400",
    ghostBtn = "border-stone-200 text-stone-900 hover:border-amber-400 hover:text-amber-700",
    primaryBtn = "bg-stone-900 text-white hover:bg-stone-800",
  } = accent;

  async function submit() {
    if (!bookingRef.trim() || !whatsapp.trim()) { setError("Both fields are required."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/reviews/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingRef, whatsapp }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      window.location.href = `/review/${json.data.token}`;
    } catch (e) { setError(e.message || "Something went wrong."); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`rounded-2xl shadow-2xl w-full max-w-sm p-7 border ${panel}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${heading}`}>Write a Review</h3>
          <button onClick={onClose} className="w-11 h-11 -mr-3 flex items-center justify-center">
            <X size={18} className="text-stone-400" />
          </button>
        </div>
        <p className="text-sm text-stone-500 mb-5">
          Stayed with us? Verify your completed stay to leave a review.
        </p>
        {googleReviewUrl && (
          <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer"
            className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full border font-bold text-sm transition-colors mb-4 ${ghostBtn}`}>
            <ExternalLink size={15} /> Review Us on Google
          </a>
        )}
        <div className="flex items-center gap-3 mb-4">
          <span className="flex-1 h-px bg-stone-200" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Or review here</span>
          <span className="flex-1 h-px bg-stone-200" />
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Booking Reference</label>
            <input type="text" value={bookingRef} onChange={e => setBookingRef(e.target.value)} placeholder="BKG-172..."
              className={`w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 ${focusRing}`} />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">WhatsApp Number Used at Booking</label>
            <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="+91 98765 43210"
              className={`w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 ${focusRing}`} />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          <button onClick={submit} disabled={loading}
            className={`w-full py-3.5 rounded-full font-bold text-sm shadow-sm transition-all disabled:opacity-60 ${primaryBtn}`}>
            {loading ? "Verifying…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
